import * as Sentry from "@sentry/react"
import {QueryClient, QueryClientProvider} from "@tanstack/react-query"
import {TRPCClientError} from "@trpc/react-query"
import {AnimatePresence} from "framer-motion"
import {FC, useCallback, useEffect, useMemo, useRef, useState} from "react"

import {
  STORAGE_KEYS,
  addHostEventListener,
  clearHashCaseId,
  dispatchHostEvent,
  dualStorageGet,
  dualStorageRemove,
  dualStorageSet,
  getHashCaseId,
  getHostBridge,
  lastSeenMessageIdKeys,
  lastUnreadMessageIdKeys,
  setHashCaseId,
} from "@frontend/hostContract"
import {SessionProvider} from "@frontend/session/provider"
import {trackEvent} from "@frontend/utils/trackEvent"
import {trpc} from "@frontend/utils/trpc"
import {TRPC_CLIENT} from "@frontend/utils/trpcClient"

import {ChatBubble} from "./ChatBubble"
import {ChatPanel} from "./ChatPanel"
import {ConversationStarters} from "./ConversationStarters"
import {getContextualStarters} from "./getContextualStarters"
import {applySlideoutLayout, removeSlideoutLayout} from "./slideoutLayout"
import {
  getInitialStarterBootstrapPayload,
  normalizeStarterBootstrapPayload,
} from "./starterBootstrap"
import {usePageContext} from "./usePageContext"

const QUERY_CLIENT = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error): boolean => {
        if (!(error instanceof TRPCClientError)) {
          return false
        }

        if (error.data?.code === "NOT_FOUND") {
          return false
        }

        return failureCount < 3
      },
    },
  },
})

const getIsInitialized = (): boolean => !!getHostBridge()?.getUserId

const useIsInitialized = (): boolean => {
  const [isInitialized, setIsInitialized] = useState(getIsInitialized())

  useEffect(() => {
    if (isInitialized) return

    const interval = setInterval(() => {
      if (getIsInitialized()) {
        setIsInitialized(true)
        clearInterval(interval)
      }
    }, 100)

    return (): void => {
      clearInterval(interval)
    }
  })

  return isInitialized
}

const getDisplayMode = (): "overlay" | "slideout" =>
  getHostBridge()?.displayMode === "slideout" ? "slideout" : "overlay"

export const ChatWidget: FC = () => {
  const isInitialized = useIsInitialized()
  const hasTrackedImpression = useRef(false)
  const hasTrackedStartersShown = useRef(false)
  const hasTrackedResumedFromLink = useRef(false)
  const latestAssistantMessageIdRef = useRef<string | null>(null)
  const lastUnreadMessageIdRef = useRef<string | null>(null)
  // Captured at mount so we can fire the resume event regardless of whether
  // the widget later rewrites the hash with its own caseId.
  const initialHashCaseIdRef = useRef<string | null>(null)
  if (initialHashCaseIdRef.current === null) {
    initialHashCaseIdRef.current = getHashCaseId()
  }
  const pageContext = usePageContext()
  // Read once - displayMode is stable for the lifetime of the widget mount.
  const [displayMode] = useState(getDisplayMode)
  const isSlideout = displayMode === "slideout"
  const startsExpanded = !getHostBridge()?.startCollapsed
  const hasRestoredCase = isSlideout || getHashCaseId() !== null
  const [isOpen, setIsOpen] = useState(() => startsExpanded && hasRestoredCase)
  const [hasEverOpened, setHasEverOpened] = useState(
    () => startsExpanded && hasRestoredCase,
  )
  const [unreadCount, setUnreadCount] = useState(0)
  const [caseId, setCaseId] = useState<string | null>(
    () =>
      getHashCaseId() ??
      dualStorageGet(STORAGE_KEYS.caseId, STORAGE_KEYS.legacyCaseId),
  )
  const [starterPayload, setStarterPayload] =
    useState<Ri2StarterBootstrapPayload | null>(() =>
      getInitialStarterBootstrapPayload(),
    )
  const [starterMessage, setStarterMessage] = useState<string | null>(
    () => getInitialStarterBootstrapPayload()?.starterMessage ?? null,
  )
  const [startersDismissed, setStartersDismissed] = useState(
    () =>
      dualStorageGet(
        STORAGE_KEYS.startersDismissed,
        STORAGE_KEYS.legacyStartersDismissed,
      ) === "true",
  )

  // Compute contextual starters from the current page context
  const starters = useMemo(
    () => getContextualStarters(pageContext),
    [pageContext],
  )

  useEffect(() => {
    if (!isSlideout) return

    if (isOpen) {
      applySlideoutLayout()
    } else {
      removeSlideoutLayout()
    }

    return (): void => {
      removeSlideoutLayout()
    }
  }, [isOpen, isSlideout])

  useEffect(() => {
    if (!caseId) {
      latestAssistantMessageIdRef.current = null
      lastUnreadMessageIdRef.current = null
      setUnreadCount(0)
      return
    }

    lastUnreadMessageIdRef.current = dualStorageGet(
      lastUnreadMessageIdKeys(caseId).primary,
      lastUnreadMessageIdKeys(caseId).legacy,
    )
    setUnreadCount(lastUnreadMessageIdRef.current ? 1 : 0)
  }, [caseId])

  useEffect(() => {
    trackEvent("chat_unread_changed", caseId ?? undefined, {unreadCount})
  }, [unreadCount, caseId])

  useEffect(() => {
    if (caseId) {
      setHasEverOpened(true)
    }
  }, [caseId])

  const onOpen = useCallback(() => {
    setHasEverOpened(true)
    setIsOpen(true)
    setUnreadCount(0)
    if (caseId) {
      const latestAssistantMessageId = latestAssistantMessageIdRef.current
      if (latestAssistantMessageId) {
        dualStorageSet(
          lastSeenMessageIdKeys(caseId).primary,
          lastSeenMessageIdKeys(caseId).legacy,
          latestAssistantMessageId,
        )
      }
      dualStorageRemove(
        lastUnreadMessageIdKeys(caseId).primary,
        lastUnreadMessageIdKeys(caseId).legacy,
      )
      lastUnreadMessageIdRef.current = null
    }
    trackEvent("widget_chat_open")
    if (isSlideout) {
      dispatchHostEvent("userExpanded")
    }
  }, [caseId, isSlideout])

  const onMinimize = useCallback(() => {
    setIsOpen(false)
    trackEvent("widget_chat_minimized")
    if (isSlideout) {
      dispatchHostEvent("userCollapsed")
    }
  }, [isSlideout])

  const onClose = useCallback(() => {
    setIsOpen(false)
    trackEvent("widget_chat_closed")
    if (isSlideout) {
      dispatchHostEvent("userCollapsed")
    }
  }, [isSlideout])

  // Let the host page collapse / expand the panel programmatically.
  useEffect(() => {
    const handleCollapse = (): void => {
      if (isOpen) {
        trackEvent("widget_chat_minimized")
        trackEvent("widget_chat_minimized_api")
      }
      setIsOpen(false)
    }
    const handleExpand = (event: Event): void => {
      const expandEvent = event as CustomEvent<
        Ri2StarterBootstrapPayload | string | null
      >
      const host = getHostBridge()
      const hostStarterPayload = normalizeStarterBootstrapPayload(
        expandEvent.detail ??
          host?.initialStarterPayload ??
          host?.initialMessage,
      )
      if (hostStarterPayload) {
        setStarterPayload(hostStarterPayload)
        setStarterMessage(hostStarterPayload.starterMessage)
      }

      if (!isOpen) {
        trackEvent("widget_chat_open")
        trackEvent("widget_chat_open_api")
      }
      setHasEverOpened(true)
      setIsOpen(true)
      setUnreadCount(0)
      if (caseId) {
        const latestAssistantMessageId = latestAssistantMessageIdRef.current
        if (latestAssistantMessageId) {
          dualStorageSet(
            lastSeenMessageIdKeys(caseId).primary,
            lastSeenMessageIdKeys(caseId).legacy,
            latestAssistantMessageId,
          )
        }
        dualStorageRemove(
          lastUnreadMessageIdKeys(caseId).primary,
          lastUnreadMessageIdKeys(caseId).legacy,
        )
        lastUnreadMessageIdRef.current = null
      }
    }

    const removeCollapse = addHostEventListener("collapse", handleCollapse)
    const removeExpand = addHostEventListener("expand", handleExpand)
    return (): void => {
      removeCollapse()
      removeExpand()
    }
  }, [caseId, isOpen])

  const onCaseCreated = useCallback((newCaseId: string) => {
    setCaseId(newCaseId)
    dualStorageSet(STORAGE_KEYS.caseId, STORAGE_KEYS.legacyCaseId, newCaseId)
    setHashCaseId(newCaseId)
  }, [])

  const onNewConversation = useCallback(() => {
    trackEvent("widget_chat_new_conversation")
    if (caseId) {
      dualStorageRemove(
        lastSeenMessageIdKeys(caseId).primary,
        lastSeenMessageIdKeys(caseId).legacy,
      )
      dualStorageRemove(
        lastUnreadMessageIdKeys(caseId).primary,
        lastUnreadMessageIdKeys(caseId).legacy,
      )
    }
    latestAssistantMessageIdRef.current = null
    lastUnreadMessageIdRef.current = null
    setUnreadCount(0)
    setCaseId(null)
    dualStorageRemove(STORAGE_KEYS.caseId, STORAGE_KEYS.legacyCaseId)
    clearHashCaseId()
  }, [caseId])

  const onUnreadMessage = useCallback(
    (messageId: string) => {
      if (isOpen || !caseId || messageId === lastUnreadMessageIdRef.current) {
        return
      }

      lastUnreadMessageIdRef.current = messageId
      dualStorageSet(
        lastUnreadMessageIdKeys(caseId).primary,
        lastUnreadMessageIdKeys(caseId).legacy,
        messageId,
      )
      setUnreadCount((currentCount) => currentCount + 1)
    },
    [caseId, isOpen],
  )

  const onLatestAssistantMessageChange = useCallback(
    (messageId: string | null) => {
      latestAssistantMessageIdRef.current = messageId

      if (!caseId || !messageId || !isOpen) {
        return
      }

      dualStorageSet(
        lastSeenMessageIdKeys(caseId).primary,
        lastSeenMessageIdKeys(caseId).legacy,
        messageId,
      )
      dualStorageRemove(
        lastUnreadMessageIdKeys(caseId).primary,
        lastUnreadMessageIdKeys(caseId).legacy,
      )
      lastUnreadMessageIdRef.current = null
    },
    [caseId, isOpen],
  )

  const onStarterSelect = useCallback((message: string) => {
    trackEvent("widget_chat_starter_selected")
    setStarterPayload({starterMessage: message})
    setStarterMessage(message)
    setHasEverOpened(true)
    setIsOpen(true)
    setUnreadCount(0)
    setStartersDismissed(true)
    trackEvent("widget_chat_open")
  }, [])

  const onStartersDismiss = useCallback(() => {
    trackEvent("widget_chat_starters_dismissed")
    dualStorageSet(
      STORAGE_KEYS.startersDismissed,
      STORAGE_KEYS.legacyStartersDismissed,
      "true",
    )
    setStartersDismissed(true)
  }, [])

  const onStarterMessageConsumed = useCallback(() => {
    setStarterPayload(null)
    setStarterMessage(null)
    const host = getHostBridge()
    if (host) {
      host.initialMessage = undefined
      host.initialStarterPayload = undefined
    }
  }, [])

  const showStarters =
    !isSlideout &&
    !isOpen &&
    !startersDismissed &&
    !caseId &&
    starters.length > 0

  useEffect(() => {
    if (!isInitialized || hasTrackedImpression.current) return
    hasTrackedImpression.current = true
    trackEvent("widget_chat_impression")
  }, [isInitialized])

  // Fire once if the page was loaded with a case id already in the URL hash.
  // That hash is only ever placed there by `setHashCaseId` AFTER a case is
  // created in-session, so at mount time a non-null value means the user
  // arrived via a shared/saved link (e.g. from the Continue Later action).
  useEffect(() => {
    if (!isInitialized || hasTrackedResumedFromLink.current) return
    const initialCaseId = initialHashCaseIdRef.current
    if (!initialCaseId) return
    hasTrackedResumedFromLink.current = true
    trackEvent("widget_chat_resumed_from_link", initialCaseId)
  }, [isInitialized])

  useEffect(() => {
    if (!showStarters || hasTrackedStartersShown.current) return
    hasTrackedStartersShown.current = true
    trackEvent("widget_chat_starters_shown")
  }, [showStarters])

  if (!isInitialized) {
    return null
  }

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTags({error_boundary: "widget"})
      }}
    >
      <trpc.Provider client={TRPC_CLIENT} queryClient={QUERY_CLIENT}>
        <QueryClientProvider client={QUERY_CLIENT}>
          <SessionProvider>
            <AnimatePresence>
              {showStarters && (
                <ConversationStarters
                  key="starters"
                  starters={starters}
                  onSelect={onStarterSelect}
                  onDismiss={onStartersDismiss}
                />
              )}
            </AnimatePresence>
            <AnimatePresence>
              {!isOpen && (
                <ChatBubble
                  key="bubble"
                  onClick={onOpen}
                  unreadCount={unreadCount}
                />
              )}
            </AnimatePresence>
            {hasEverOpened && (
              <ChatPanel
                displayMode={displayMode}
                isOpen={isOpen}
                caseId={caseId}
                starterPayload={starterPayload}
                starterMessage={starterMessage}
                onStarterMessageConsumed={onStarterMessageConsumed}
                onCaseCreated={onCaseCreated}
                onNewConversation={onNewConversation}
                onMinimize={onMinimize}
                onClose={onClose}
                onUnreadMessage={onUnreadMessage}
                onLatestAssistantMessageChange={onLatestAssistantMessageChange}
              />
            )}
          </SessionProvider>
        </QueryClientProvider>
      </trpc.Provider>
    </Sentry.ErrorBoundary>
  )
}
